import React, { useState } from 'react';
import {
    Typography,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Button,
    IconButton,
    Collapse,
    Select,
    MenuItem,
    FormControl,
    Checkbox,
    ListItemText,
    TextField,
    Chip,
    Tooltip,
    TablePagination,
    TableSortLabel
} from '@material-ui/core';
import { observer } from 'mobx-react-lite';
import { ExpandMore, ExpandLess, CheckCircleOutline, Sync, Group } from '@material-ui/icons';
import auditDataStore from '../../stores/license/auditDataStore';
import { LicenseReviewDTO } from "../../stores/models/licenseModels";
import './index.css';

const LicenseFormReviewTable: React.FC = observer(() => {
    const [openRows, setOpenRows] = useState<{ [key: string]: boolean }>({});
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');
    const [orderBy, setOrderBy] = useState<string>('name');

    const handleRowClick = (email: string) => {
        setOpenRows(prevState => ({ ...prevState, [email]: !prevState[email] }));
    };

    const handleSelectChange = (actionId: string, event: React.ChangeEvent<{ value: unknown }>) => {
        const value = event.target.value;

        const selectedOptions = Array.isArray(value) ? value as string[] : [value as string];
        auditDataStore.updateSelectedOptions(actionId, selectedOptions);
    };

    const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newComment = event.target.value;
        auditDataStore.updateComment(newComment);
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRequestSort = (property: string) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const allActionsHaveSelectedOptions = (review: LicenseReviewDTO) => {
        return review.licenseReviewActions.every(action => action.selectedOptions.length > 0);
    };

    const sortedReviews = auditDataStore.currentFormState.licenseReviews.slice().sort((a, b) => {
        if (orderBy === 'name') {
            return (a.name < b.name ? -1 : 1) * (order === 'asc' ? 1 : -1);
        } else if (orderBy === 'email') {
            return (a.email < b.email ? -1 : 1) * (order === 'asc' ? 1 : -1);
        } else if (orderBy === 'title') {
            return (a.title < b.title ? -1 : 1) * (order === 'asc' ? 1 : -1);
        } else if (orderBy === 'status') {
            const aStatus = allActionsHaveSelectedOptions(a) ? 'Completed' : 'Pending';
            const bStatus = allActionsHaveSelectedOptions(b) ? 'Completed' : 'Pending';
            return (aStatus < bStatus ? -1 : 1) * (order === 'asc' ? 1 : -1);
        } else {
            return 0;
        }
    });

    const handleFetchPreviousOptions = async () => {
        await auditDataStore.fetchPreviousOptions();
    };

    if (auditDataStore.currentFormState.licenseReviews.length === 0) {
        return <></>;
    }

    return (
        <div id="license-form-wrapper">
            {auditDataStore.loading && <CircularProgress />}
            {auditDataStore.fetchError && (
                <Typography variant="body1" color="error" gutterBottom>
                    {auditDataStore.fetchError}
                </Typography>
            )}
            {auditDataStore.auditStats && auditDataStore.selectedAudit && !auditDataStore.selectedAudit.isCompleted && (
                <Box textAlign="center" mb={2} display="flex" justifyContent="flex-end">
                    <Tooltip title="Loads the form with the selected options from the previous audit">
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleFetchPreviousOptions}
                            disabled={!auditDataStore.selectedAudit || auditDataStore.selectedAudit.isCompleted}
                        >
                            Load Previous Options
                        </Button>
                    </Tooltip>
                </Box>
            )}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ width: '50px' }} />
                            <TableCell>
                                <TableSortLabel
                                    active={orderBy === 'name'}
                                    direction={orderBy === 'name' ? order : 'asc'}
                                    onClick={() => handleRequestSort('name')}
                                >
                                    Name
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={orderBy === 'email'}
                                    direction={orderBy === 'email' ? order : 'asc'}
                                    onClick={() => handleRequestSort('email')}
                                >
                                    Email
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={orderBy === 'title'}
                                    direction={orderBy === 'title' ? order : 'asc'}
                                    onClick={() => handleRequestSort('title')}
                                >
                                    Title
                                </TableSortLabel>
                            </TableCell>
                            <TableCell style={{ width: '50px' }}>
                                <TableSortLabel
                                    active={orderBy === 'status'}
                                    direction={orderBy === 'status' ? order : 'asc'}
                                    onClick={() => handleRequestSort('status')}
                                >
                                    Status
                                </TableSortLabel>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedReviews
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((review) => (
                                <React.Fragment key={review.email}>
                                    <TableRow>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => handleRowClick(review.email)}>
                                                {openRows[review.email] ? <ExpandLess /> : <ExpandMore />}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>{review.name}</TableCell>
                                        <TableCell>{review.email}</TableCell>
                                        <TableCell>{review.title}</TableCell>
                                        <TableCell>
                                            {allActionsHaveSelectedOptions(review) ?
                                                <CheckCircleOutline style={{ color: 'green' }} /> : <></>}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell colSpan={4} style={{ paddingBottom: 0, paddingTop: 0 }}>
                                            <Collapse in={openRows[review.email] || review.licenseReviewActions.some(action => auditDataStore.incompleteActions.includes(action.id))} timeout="auto" unmountOnExit>
                                                <Box margin={1}>
                                                    <Table size="small" aria-label="actions">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell className="question-column">Question</TableCell>
                                                                <TableCell className="options-column">Selected Options</TableCell>
                                                                <TableCell className="apply-options-column" style={{ minWidth: '80px' }}>
                                                                    <Tooltip title="Apply this person's selected options to all other employees">
                                                                        <IconButton
                                                                            color="primary"
                                                                            onClick={() => auditDataStore.handleApplyOptions(review.licenseReviewActions)}
                                                                            disabled={!auditDataStore.selectedAudit || auditDataStore.selectedAudit.isCompleted}
                                                                        >
                                                                            <Group />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Apply this person's selected options to others with the same title">
                                                                        <IconButton
                                                                            color="primary"
                                                                            onClick={() => auditDataStore.handleApplyOptions(review.licenseReviewActions, review.title, review.email)}
                                                                            disabled={!auditDataStore.selectedAudit || auditDataStore.selectedAudit.isCompleted}
                                                                        >
                                                                            <Sync />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {review.licenseReviewActions.map(action => {
                                                                const actionDetails = auditDataStore.auditStats?.licenseAuditActions.find(a => a.id === action.actionId);
                                                                return (
                                                                    <TableRow key={action.id}>
                                                                        <TableCell>{actionDetails?.question}</TableCell>
                                                                        <TableCell>
                                                                            <FormControl fullWidth style={{ border: auditDataStore.incompleteActions.includes(action.id) ? '1px solid red' : 'none' }}>
                                                                                <Select
                                                                                    multiple={actionDetails?.isMultiSelect}
                                                                                    value={actionDetails?.isMultiSelect ? action.selectedOptions : (action.selectedOptions[0] || '')}
                                                                                    onChange={(event) => handleSelectChange(action.id, event)}
                                                                                    renderValue={(selected) => (
                                                                                        <Box display="flex" flexWrap="wrap">
                                                                                            {actionDetails?.isMultiSelect ? (
                                                                                                // Multi-select: selected is an array
                                                                                                (selected as string[]).map((value) => {
                                                                                                    const optionName = auditDataStore.getOptionNamesByIds([value])[0];
                                                                                                    return (
                                                                                                        <Box key={value} m={0.5}>
                                                                                                            <Tooltip title={optionName}>
                                                                                                                <Chip label={optionName} style={{ maxWidth: 300 }} />
                                                                                                            </Tooltip>
                                                                                                        </Box>
                                                                                                    );
                                                                                                })
                                                                                            ) : (
                                                                                                // Single-select: selected is a single value
                                                                                                selected && (
                                                                                                    <Box m={0.5}>
                                                                                                        <Tooltip title={auditDataStore.getOptionNamesByIds([selected as string])[0]}>
                                                                                                            <Chip label={auditDataStore.getOptionNamesByIds([selected as string])[0]} style={{ maxWidth: 300 }} />
                                                                                                        </Tooltip>
                                                                                                    </Box>
                                                                                                )
                                                                                            )}
                                                                                        </Box>
                                                                                    )}
                                                                                    MenuProps={{
                                                                                        anchorOrigin: {
                                                                                            vertical: 'bottom',
                                                                                            horizontal: 'left',
                                                                                        },
                                                                                        transformOrigin: {
                                                                                            vertical: 'top',
                                                                                            horizontal: 'left',
                                                                                        },
                                                                                        getContentAnchorEl: null,
                                                                                        PaperProps: {
                                                                                            style: {
                                                                                                maxWidth: 1000,
                                                                                            },
                                                                                        }
                                                                                    }}
                                                                                    disabled={auditDataStore.selectedAudit?.isCompleted}
                                                                                >
                                                                                    {actionDetails?.licenseActionOptions.map(option => (
                                                                                        <MenuItem key={option.id} value={option.id} className="option-text-wrap">
                                                                                            {actionDetails?.isMultiSelect &&
                                                                                                <Checkbox checked={action.selectedOptions.indexOf(option.id) > -1} />}
                                                                                            <ListItemText primary={option.option} />
                                                                                        </MenuItem>
                                                                                    ))}
                                                                                </Select>
                                                                            </FormControl>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                );
                                                            })}
                                                        </TableBody>
                                                    </Table>
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={auditDataStore.currentFormState.licenseReviews.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>
            <Box mt={2}>
                <TextField
                    multiline
                    minRows={1}
                    variant="outlined"
                    fullWidth
                    value={auditDataStore.currentFormState.comment || ''}
                    onChange={handleCommentChange}
                    placeholder={auditDataStore.initialFormState.comment || ''}
                    InputProps={{
                        style: { backgroundColor: 'white' }
                    }}
                    inputProps={{ maxLength: 2000 }}
                    label="Comment"
                    disabled={auditDataStore.selectedAudit?.isCompleted}
                />
            </Box>
        </div>
    );
});

export default LicenseFormReviewTable;